<template>
	<div>
		<canvas id="sankey-chart" height="190px"></canvas>
	</div>
</template>
<script>
import { Chart } from "chart.js";
import "chart.js-plugin-labels-dv";
import * as Sankey from "chartjs-chart-sankey";
import AnalysisTooltip from "./analysis_tooltip.js";

Chart.register(Sankey.SankeyController, Sankey.Flow);

export default {
	name: "sankey-chart",
	props: {
		showLabels: Boolean,
		chartId: String,
		srvPhaseId: Number,
		perspectiveActorId: Number,
		propertyTypeKeyPart: String,
		apValueColumn: String,
	},
	computed: {
		chartOptions() {
			const analyisTooltip = _.cloneDeep(AnalysisTooltip); // Clone
			// Overwrite default
			analyisTooltip.callbacks.label = (datasetForIndex) => {
				const dataForItem =
					datasetForIndex.dataset.data[datasetForIndex.dataIndex];
				// pp.log("label", datasetForIndex, dataForItem);
				const number = window.numeral(dataForItem.flow);
				let formattedValue = number.format(
					// @ts-expect-error
					number >= 10000 ? window.numeral.formatWithUnit : "",
				);
				if (datasetForIndex.dataset.value_unit) {
					formattedValue += ` ${datasetForIndex.dataset.value_unit}`;
				}
				return `${dataForItem.from} → ${dataForItem.to}: ${formattedValue} ${I18n.t(dataForItem.isSurplus ? "analysis.trade_surplus" : "analysis.trade_deficit")}`;
			};
			return {
				plugins: {
					legend: {
						display: false,
					},
					tooltip: analyisTooltip,
				},
				color: "#676767", // LESS @text-color
			};
		},
		phaseId() {
			return this.srvPhaseId !== undefined
				? this.srvPhaseId
				: pp.gameState.phaseId;
		},
		sortedActors() {
			const sortedActors = Object.values(VueStore.getters.gameActors()).sort(
				(firstActor, secondActor) => {
					return firstActor.names[I18n.locale] < secondActor.names[I18n.locale]
						? -1
						: 1;
				},
			);
			return sortedActors.reduce(function (map, actor, index) {
				map[actor.names[I18n.locale]] = index;
				return map;
			}, {});
		},
		chartData() {
			const gameActors = VueStore.getters.gameActors();
			const perspectiveActor = gameActors[this.perspectiveActorId];
			const sankeyChartData = [];
			let valueUnit;
			VueStore.getters
				.actorsPropertiesFor(this.perspectiveActorId)
				.forEach((actorProperty) => {
					if (!valueUnit) {
						valueUnit = actorProperty.property.value_unit;
					}
					// Check if this is a trade_balance ActorProperty (as defined by `propertyTypeKeyPart`, but not the overall 'trade_balance' ActorProperty)
					if (
						actorProperty.property.type_key != this.propertyTypeKeyPart &&
						actorProperty.property.type_key.indexOf(this.propertyTypeKeyPart) ==
							0
					) {
						const otherActorCountryCodeFromTypeKey =
							actorProperty.property.type_key.slice(
								this.propertyTypeKeyPart.length + 1,
							);
						const otherActor = VueStore.getters.actorWithAcronym(
							otherActorCountryCodeFromTypeKey,
							I18n.locale,
						);
						if (!otherActor) {
							return;
						}
						const phaseVal =
							actorProperty.values[this.phaseId][this.apValueColumn];
						// pp.log("val", phaseVal, otherActor.names[I18n.locale], perspectiveActor.names[I18n.locale])
						if (phaseVal < 0) {
							// Deficit
							sankeyChartData.push({
								fromActorId: otherActor.id,
								toActorId: perspectiveActor.id,
								from: otherActor.names[I18n.locale],
								to: perspectiveActor.names[I18n.locale],
								flow: phaseVal * -1,
							});
						} else {
							// Deficit
							sankeyChartData.push({
								fromActorId: perspectiveActor.id,
								toActorId: otherActor.id,
								isSurplus: true,
								from: perspectiveActor.names[I18n.locale],
								to: otherActor.names[I18n.locale],
								flow: phaseVal,
							});
						}
					}
				});

			return {
				datasets: [
					{
						data: sankeyChartData,
						value_unit: valueUnit,
						colorFrom: (c) =>
							this.getColor(c.dataset.data[c.dataIndex].fromActorId),
						colorTo: (c) =>
							this.getColor(c.dataset.data[c.dataIndex].toActorId),
						alpha: 0.66,
						priority: this.sortedActors,
					},
				],
			};
		},
	},
	methods: {
		getColor(actorId) {
			const actorColorClass = VueStore.getters.actorData(actorId).color;
			return pp.viewHelpers.detectCssRuleFor(
				`.${actorColorClass}`,
				"background-color",
			);
		},
	},
	mounted() {
		// pp.log("mount", this);
		// @ts-expect-error
		const context = document.getElementById("sankey-chart").getContext("2d");

		// Declare here so that it can be accessed below
		// @ts-expect-error
		this.chart = new Chart(context, {
			type: "sankey",
			data: this.chartData,
			// @ts-expect-error
			options: this.chartOptions,
		});

		pp.vue.bus.$on("CHART-datasets_changed", (data) => {
			if (data.chartId == this.chartId) {
				// pp.log("change", this.chartId, data);
				if (data.perspectiveActorId) {
					// @ts-expect-error
					this.perspectiveActorId = parseInt(data.perspectiveActorId, 10);
				}
				if (data.phaseId) {
					// @ts-expect-error
					this.srvPhaseId = parseInt(data.phaseId, 10);
				}
			}
		});
	},
	watch: {
		// Need to watch the chartData which will be changed when perspectiveActorId or srvPhaseId are changed.
		// Normally this is beign done by the `reactiveProp` mixin from Vue but it doesn't work with the sankey chart
		chartData: function () {
			// @ts-expect-error
			this.chart.data.datasets[0].data = this.chartData.datasets[0].data;
			// this.chart.reset();
			this.chart.update();
		},
	},
	beforeDestroy() {
		pp.vue.bus.$off("CHART-datasets_changed");
	},
};
</script>
